import {useDispatch, useSelector} from 'react-redux';
import { BusinessAction, useBusinessRulesEngine } from '../Data/BusinessRulesData';
import { ApplicationDispatch, SET_SERVER_MESSAGE } from '../store/application/types';
import { canApproveDenyApp, getApplicant, getRuleViolationsByType, validateApp } from '../../src/utils/appUtils';
import { IApplication } from '../Interfaces/Application/IApplication';
import { IRuleViolation } from '../Interfaces/Application/IApplicationQuote';
import { Constants } from '../utils/constants';
import { saveApp, sendDecisionToDealerTrackAndSaveApp, updateEditingApp } from '../store/application/actions';
import { IApplicationNotes } from '../Interfaces/Application/IApplicationNotes';
import produce from 'immer';
import { hasValue } from "@amsanalytics/react-shared-library";
import { IndirectDecisionType } from '../Data/IndirectDecisionType';
import { IndirectDealerApplicationData, useDecisionDialog } from '../Components/Dialogs/IndirectDealerDecisionDialog/IndirectDealerDecisionDialog';
import {AppState} from "../store/rootReducer";
import { IndirectQuoteType } from '../Data/IndirectQuoteType';
import { calculateBalloonPayment, calculateLTV } from '../utils/quoteUtils';
import { usePricingPrograms } from '../Data/LookupData';
import {IDealerDecisionData} from "../Interfaces/Common/IDealerDecisionData";
import { ApplicationQuoteType } from '@amsanalytics/react-shared-library';

export const useDecisions = (
  financeQuoteErrors: IRuleViolation[]
): [
    (
      bypassAppRequirements: boolean,
      useInternalAmsScore: boolean | undefined,
      validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
    ) => void,
    (
      appToSave: IApplication,
      newStatusId: string,
      useInternalAmsScore: boolean | undefined,
      userBypassedSoftPullRequirement: boolean,
      action?: BusinessAction,
      validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void,
      indirectDecisionType?: IndirectDecisionType,
      requestNotes?: boolean,
      requestDocuments?: boolean,
      requestNotesPrompt?: string,
      requestNotesOrDocumentsCallback?: (statusChangeCallback: (applicationNotes?: IApplicationNotes[]) =>
        void, requestNotes?: boolean, requestDocuments?: boolean, requestNotesPrompt?: string) => void
    ) => void,
    (
      bypassAppRequirements: boolean,
      useInternalAmsScore: boolean | undefined,
      validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
    ) => void,
    (
      bypassAppRequirements: boolean,
      useInternalAmsScore: boolean | undefined,
      validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
    ) => void,
  ] => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const [openDecisionDialog] = useDecisionDialog(currentApp!);
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const { data: pricingPrograms } = usePricingPrograms(); 
  const decisionCodes = useSelector((state: AppState) => state.applications.lookups.dealerServiceDecisionCodes);

  const handleStatusChange = async (statusId: string, application: IApplication, applicationNotes: IApplicationNotes[] | undefined, userBypassedSoftPullRequirement: boolean) => {
    const errors: string[] = validateApp(application);
    if (errors.length > 0) {
      const errs: string = errors.join(" ");
      dispatch({
        type: SET_SERVER_MESSAGE, payload: {
          isError: true,
          message: errs,
        }
      });
    } else {
      const updatedApp = produce(application, (appToUpdate) => {
        if (applicationNotes && applicationNotes.length > 0) {
          appToUpdate!.applicationNotes = [...appToUpdate.applicationNotes, ...applicationNotes];
        }
        appToUpdate.appStatusId = statusId;
      });
      dispatch(updateEditingApp(updatedApp, businessRulesEngine));
      await dispatch(saveApp(businessRulesEngine, userBypassedSoftPullRequirement));
    }
  };

  const errorRuleViolations: IRuleViolation[] = getRuleViolationsByType(currentApp, financeQuoteErrors, 'error');
	const autoDenySubmissionRuleViolations: IRuleViolation[] = getRuleViolationsByType(currentApp, financeQuoteErrors, 'auto-deny-on-submit');
  const getDecisionCodeForStatusAndAppSource = (statusId: string, appSource: string): string => {
    const matchingDecisionCode = decisionCodes?.find(code => code.appStatusId === statusId && code.appSource === appSource);
    return matchingDecisionCode!.code;
  };

  const getDecisionCodeIdForStatusAndAppSource = (statusId: string, appSource: string): string | undefined => {
    const matchingDecisionCode = decisionCodes?.find(code => code.appStatusId === statusId && code.appSource === appSource);
    return matchingDecisionCode?.id;
  };

  const updateApplicationStatus = async (
    appToSave: IApplication,
    newStatusId: string,
    useInternalAmsScore: boolean | undefined,
    userByPassedHardPullRequirement: boolean,
    businessRuleAction?: BusinessAction,
    validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void,
    indirectDecisionType?: IndirectDecisionType,
    requestNotes?: boolean,
    requestDocuments?: boolean,
    requestNotesPrompt?: string,
    requestNotesOrDocumentsCallback?: (statusChangeCallback: (applicationNotes?: IApplicationNotes[]) => void, requestNotes?: boolean, requestDocuments?: boolean, requestNotesPrompt?: string) => void,
  ): Promise<void> => {
    // TODO: Verify that if bypassAppRequirements is true and validateErrors is undefined then throw error.
    const errors: string[] = validateApp(appToSave!);
    const indirectUnderwriterQuote = (appToSave.quoteType === ApplicationQuoteType.Indirect) && appToSave.applicationQuotes.find(aq => aq.quote.quoteType === IndirectQuoteType.Underwriter)?.quote;
    const ficoScore = getApplicant(appToSave, Constants.roleApplicantId, true)?.customer?.customerCredit?.[0]?.bureauScore;
    const ltvRatio = indirectUnderwriterQuote
      ? calculateLTV(indirectUnderwriterQuote, appToSave.vehicle, false) as number ?? 0
      : 0;

    // Instead of calling the hook here, use the data from the top-level hook call
    const tierLevel = indirectUnderwriterQuote && pricingPrograms?.find(x => x.id === indirectUnderwriterQuote?.pricingProgramId)?.name;

    const result = hasValue(businessRuleAction)
      ? businessRulesEngine({
        action: businessRuleAction!,
      })
      : undefined;

    const autoDenyRuleViolations = [...autoDenySubmissionRuleViolations,
    ...result?.errors?.filter(e => (e as IRuleViolation)?.type === 'auto-deny-on-submit') ?? []];

    // Note length of 0 will return falsy.
    if (autoDenyRuleViolations.length > 0 && newStatusId.toUpperCase() === Constants.applicationStatuses.underwriting) {
      // TODO: Need to know which status is a denial status from the applicationStatus list.               
      // Currently calling denyApp, but we need to just set the status ourselves to the know denial status
      // BJ 03/30/2021 - TODO
      denyApplication(useInternalAmsScore);
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: false,
          message: `The application was automatically denied for the following reasons: ${autoDenyRuleViolations.map(rv => (rv as IRuleViolation)?.message).join(', ')}`,
        },
      })
    } else if (errors.length > 0) {
      const errs: string = errors.join(' ');
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: true,
          message: errs,
        },
      });
    } else if (indirectUnderwriterQuote &&  (!indirectDecisionType || indirectDecisionType === IndirectDecisionType.OpenDecisionDialog)) {
      openDecisionDialog(newStatusId);
    } else if (indirectUnderwriterQuote && tierLevel && indirectDecisionType === IndirectDecisionType.SendAutomatically) {

      // Pull out the Decision Code from the list based on the ApplicationStatus
      const decisionInfo: IndirectDealerApplicationData = {
         decisionCode: getDecisionCodeForStatusAndAppSource(newStatusId, appToSave.appSource!), 
         decisionCodeId: getDecisionCodeIdForStatusAndAppSource(newStatusId, appToSave.appSource!),
         analystComments: [] 
        };       
      
      // Post the new status to Dealertrack or RouteOne automatically
      const decisionData: IDealerDecisionData = {
        applicationId: appToSave.id,
        underwriterId: appToSave.applicationAssignments.find(a => a.assignmentEmployeeRoleId === Constants.roles.underwriterId)?.assignedToId!,
        buyRate: indirectUnderwriterQuote.apr!,
        ltvRatio: ltvRatio ?? 0,
        ficoScore: ficoScore!,
        amountToFinance: indirectUnderwriterQuote.amountToFinance!,
        tierLevel: tierLevel!,
        termInMonths: indirectUnderwriterQuote.term,
        acquisitionFee: indirectUnderwriterQuote.bankFee,
        decisionCode: decisionInfo.decisionCode,
        decisionCodeId: decisionInfo.decisionCodeId,
        analystComments: decisionInfo.analystComments,
        balloonPayment: calculateBalloonPayment(indirectUnderwriterQuote) ?? 0,
        payment: indirectUnderwriterQuote.payment!,
        cashDown: indirectUnderwriterQuote.cashDown,
        tradeAllowance: indirectUnderwriterQuote.tradeAllowance,
        carSalePrice: indirectUnderwriterQuote.carSalePrice,
        paymentFrequency: indirectUnderwriterQuote?.quotePayTypeId,
        conditionedOrDeclinedReasons: [],
        stipulations: [],
      };
      
      dispatch(sendDecisionToDealerTrackAndSaveApp(decisionData, appToSave!, userByPassedHardPullRequirement));
    }
    else if (newStatusId?.toUpperCase() === Constants.applicationStatuses.denied) {
      if (requestNotesOrDocumentsCallback && (requestNotes || requestDocuments)) {
        requestNotesOrDocumentsCallback((applicationNotes?: IApplicationNotes[]) =>
          denyApplication(useInternalAmsScore, applicationNotes), requestNotes, requestDocuments, requestNotesPrompt);
      } else {
        denyApplication(useInternalAmsScore);
      }
    } else {
      if (result?.errors?.length || errorRuleViolations.length > 0) {
        validateErrors &&
          validateErrors([...(result?.errors ?? []), ...errorRuleViolations], () => {
            // If the list of auto-denials is > 0, we need to automatically deny this app 
            // If documents or notes are requested, then need to use requestNotesOrDocumentsCallback
            if (requestNotesOrDocumentsCallback && (requestNotes || requestDocuments)) {
              requestNotesOrDocumentsCallback((applicationNotes?: IApplicationNotes[]) =>
                handleStatusChange(newStatusId, appToSave!, applicationNotes, false), requestNotes, requestDocuments, requestNotesPrompt);
            } else {
              handleStatusChange(newStatusId, appToSave!, [], false);
            }
          }
          );
      } else {
        if (requestNotesOrDocumentsCallback && (requestNotes || requestDocuments)) {
          requestNotesOrDocumentsCallback((applicationNotes?: IApplicationNotes[]) =>
            handleStatusChange(newStatusId, appToSave!, applicationNotes, userByPassedHardPullRequirement), requestNotes, requestDocuments, requestNotesPrompt);
        } else {
          handleStatusChange(newStatusId, appToSave!, [], userByPassedHardPullRequirement);
        }
      }
    }
  };

  const submitApp = async (
    bypassAppRequirements: boolean,
    useInternalAmsScore: boolean | undefined,
    validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
  ) => {
    // TODO: Verify that if bypassAppRequirements is true and validateErrors is undefined then throw error.
    const errors: string[] = validateApp(currentApp!);
    if (errors.length > 0) {
      const errs: string = errors.join(' ');
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: true,
          message: errs,
        },
      });
    } else {
      const result = businessRulesEngine({
        action: 'Submit',
      });
      // Note length of 0 will return falsy.
      if (result?.errors?.length && !bypassAppRequirements) {
        validateErrors &&
          validateErrors([...result.errors, ...autoDenySubmissionRuleViolations], () => {
            denyApp(true, useInternalAmsScore);
          });
      } else {
        if (autoDenySubmissionRuleViolations?.length > 0) {
          denyApp(true, useInternalAmsScore);
        } else {
          currentApp!.appStatusId = Constants.applicationStatuses.underwriting;
          await dispatch(saveApp(businessRulesEngine, false));
        }
      }
    }
  };

  const approveApp = (
    bypassAppRequirements: boolean,
    useInternalAmsScore: boolean | undefined,
    validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
  ) => {
    // TODO: Verify that if bypassAppRequirements is true and validateErrors is undefined then throw error.
    const errors: string[] = canApproveDenyApp(currentApp!, true, useInternalAmsScore);
    if (errors.length > 0) {
      const errs: string = errors.join(', ');
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: true,
          message: errs,
        },
      });
    } else {
      const result = businessRulesEngine({
        action: 'Approve',
      });

      if (result?.errors?.length && !bypassAppRequirements) {
        validateErrors &&
          validateErrors([...result.errors, ...errorRuleViolations], () => {
            handleStatusChange(Constants.applicationStatuses.approved, currentApp!, [], false);
          });
      } else {
        handleStatusChange(Constants.applicationStatuses.approved, currentApp!, [], false);
      }
    }
  };

  /**
   * @deprecated this is needed for old status workflow we are working to move all clients off that mdw 120922
   */
  //commented out validateErrors portion as it was not being passed in anywhere
  const denyApp = (
    bypassAppRequirements: boolean,
    useInternalAmsScore: boolean | undefined,
    // validateErrors?: (errors: (string | IRuleViolation)[], bypassErrorsCallback: () => void) => void
  ) => {
    // TODO: Verify that if bypassAppRequirements is true and validateErrors is undefined then throw error.
    const errors: string[] = canApproveDenyApp(currentApp!, true, useInternalAmsScore);
    if (errors.length > 0) {
      const errs: string = errors.join(', ');
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: true,
          message: errs,
        },
      });
    } else {
      const result = businessRulesEngine({
        action: 'Deny',
      });

      if (result?.errors?.length && !bypassAppRequirements) {
        // validateErrors &&
        //   validateErrors([...result.errors, ...errorRuleViolations], () => {
        //     handleStatusChange(Constants.applicationStatuses.denied, application);
        //   });
      } else {
        handleStatusChange(Constants.applicationStatuses.denied, currentApp!, [], false);
      }
    }
  };

  const denyApplication = (
    useInternalAmsScore: boolean | undefined, applicationNotes?: IApplicationNotes[]
  ) => {
    // TODO: Verify that if bypassAppRequirements is true and validateErrors is undefined then throw error.
    const errors: string[] = canApproveDenyApp(currentApp!, true, useInternalAmsScore);
    if (errors.length > 0) {
      const errs: string = errors.join(', ');
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: {
          isError: true,
          message: errs,
        },
      });
    } else {
      const result = businessRulesEngine({
        action: 'Deny',
      });
      handleStatusChange(Constants.applicationStatuses.denied, currentApp!, applicationNotes, false);
    }
  };

  return [submitApp, updateApplicationStatus, approveApp, denyApp];
};
